import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { UpcomingOpenIcon } from '../../../../components/icons/UpcomingIcon'
import { db } from '../../../../lib/db/indexeddb'
import { scheduleSync } from '../../../../lib/sync/sync-engine'
import type { Task } from '../../../../lib/db/models'
import { TaskEntry, type TaskCreationData, type TaskEntryData } from '../../components/TaskEntry'
import { useMidnightRefresh } from '../../hooks/useMidnightRefresh'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { cloneTaskFields } from '../../utils/cloneTaskFields'
import { MonthSelector } from './components/MonthSelector'
import { MonthTitle } from './components/MonthTitle'
import { MonthBlock } from './components/MonthBlock'
import { useCompletionGrace } from './hooks/useCompletionGrace'
import { useUpcomingTasks } from './hooks/useUpcomingTasks'
import { useVisibleMonth } from './hooks/useVisibleMonth'
import { useVisibleMonthRange } from './hooks/useVisibleMonthRange'
import {
  addMonths,
  dateKey,
  monthKey,
  startOfDay,
  startOfMonth,
} from './hooks/upcomingTasks.utils'
import type { UpcomingPageProps } from './UpcomingPage.types'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import '../TaskViews.scss'
import './UpcomingPage.scss'

// Pixel distance from top/bottom of scroll container at which we extend the
// rendered month range. Larger = earlier load; smaller = closer to viewport.
const LAZY_LOAD_THRESHOLD_PX = 400


// Height of the top/bottom fade overlays on the scroll area (mirrors
// `.upcoming-page__fade-*` height in SCSS). Keyboard selections must land
// outside this band, plus a visual breathing margin, so the selected row
// isn't hidden under the blur gradient — generous enough that the row looks
// clearly above the fade, not just technically outside it.
const FADE_HEIGHT_PX = 48
const KEYBOARD_SCROLL_MARGIN_PX = 12

// Extra vertical nudge below the title offset so the first day row isn't
// crammed right up against the fade edge after a chip jump / initial scroll.
const SCROLL_JUMP_PADDING_PX = -16

/**
 * Scroll `scrollEl` so that the month-block under `anchorEl` starts at the
 * top of the viewport WITH its inline title already pushed above the top
 * edge — the first day row then sits directly below the fade overlay.
 *
 * Without the title offset, the inline MonthTitle would linger inside the
 * fade band, showing "April" twice (pinned + inline-under-blur).
 */
function scrollMonthIntoPosition(scrollEl: HTMLElement, anchorEl: HTMLElement): void {
  const rootRect = scrollEl.getBoundingClientRect()
  const anchorRect = anchorEl.getBoundingClientRect()
  const titleEl = anchorEl.querySelector<HTMLElement>('.month-block__title')
  const titleHeight = titleEl?.offsetHeight ?? 0
  scrollEl.scrollTop += (anchorRect.top - rootRect.top) + titleHeight + SCROLL_JUMP_PADDING_PX
}

export const UpcomingPage: React.FC<UpcomingPageProps> = ({ lifeSpace }) => {
  const { t } = useTranslation()
  const isActive = useIsActiveTab()

  // ── Today (frozen per mount; midnight-refresh would invalidate initial state). ──
  const today = useMemo(() => startOfMonth(new Date()), [])

  // Single subscription to the midnight timer so all day rows update
  // the "today" highlight in sync without each DayGroup subscribing
  // independently (used to be N parallel setTimeout listeners).
  useMidnightRefresh()
  const todayDateKey = useMemo(() => dateKey(startOfDay(new Date())), [])

  // ── Local state ──
  const [selectedMonth, setSelectedMonth] = useState<Date>(today)
  const [visibleMonthKey, setVisibleMonthKey] = useState<string>(() => monthKey(today))

  // Ref mirror for IO — useVisibleMonth reads `anchorsRef.current` which is a
  // Map populated by each MonthBlock on mount.
  const anchorsRef = useRef<Map<string, HTMLElement | null>>(new Map())
  const scrollRef = useRef<HTMLDivElement>(null)

  // ── Lazy range ──
  // Small initial window — cold-mount of UpcomingPage previously committed
  // 7 MonthBlocks × ~30 DayGroups × N DraggableTaskEntry hooks in a single
  // synchronous pass (measured: 438 ms nested-update). Three months are
  // plenty to cover the scroll-to-today viewport; the auto-fill effect
  // below + scroll triggers extend the range outward on demand.
  const { range, extendUp, extendDown, resetTo } = useVisibleMonthRange({
    initialCenter: today,
    initialPadding: 1,
  })

  // ── Completion grace window ──
  const { graceIds, markCompleted } = useCompletionGrace()

  // ── Data ──
  // `setOptimisticTaskOverride` lets badge updates AND drag-and-drop
  // render the change instantly instead of waiting on the IDB write →
  // LiveQuery → re-render roundtrip (~1–2s with sync cycles in flight).
  // The hook owns and auto-clears each override key once Dexie confirms.
  const { months, setOptimisticTaskOverride } = useUpcomingTasks({
    lifeSpace,
    startMonth: range.start,
    endMonth: range.end,
    graceIds,
    isActive,
  })

  // ── Flat snapshot of all rendered tasks (for in-memory lookups) ──
  // Used by useTaskOperations so the optimistic toggle / delete can
  // read each task's current `status` without an extra `db.getTask`.
  const flatTasks = useMemo(() => {
    const out: Task[] = []
    for (const m of months) for (const d of m.days) for (const tk of d.tasks) out.push(tk)
    return out
  }, [months])

  // ── Task ops ──
  // Wired with the optimistic-override sink so toggle/inline-edit/delete
  // paint in the next paint instead of waiting on the IDB → LiveQuery
  // roundtrip. The grace-window wrapper for toggle stays so completed
  // tasks linger briefly before disappearing from the Upcoming list.
  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: flatTasks,
  })

  const wrappedToggleComplete = useCallback((taskId: string) => {
    markCompleted(taskId)
    handleToggleComplete(new Set([taskId]))
  }, [handleToggleComplete, markCompleted])

  const wrappedDelete = useCallback((taskId: string) => {
    handleDeleteTask(new Set([taskId]))
  }, [handleDeleteTask])

  // Inline badge update (start date, deadline, duration, attention, tags) —
  // bypasses the toolbar flow because this page has no multi-selection.
  // Optimistic-first so the badge change is visible in the next paint
  // instead of waiting on the IDB → LiveQuery roundtrip (~1–2s).
  const handleBadgeUpdate = useCallback(async (taskId: string, updates: Partial<Omit<Task, 'id'>>) => {
    setOptimisticTaskOverride(taskId, updates)
    await db.updateTask(taskId, updates)
    scheduleSync()
  }, [setOptimisticTaskOverride])

  // ── Editing state (page-wide so only one task is expanded at a time) ──
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const startEdit = useCallback((taskId: string) => setEditingTaskId(taskId), [])
  const cancelEdit = useCallback(() => setEditingTaskId(null), [])

  // ── Selection (single-task, page-wide) ──
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const selectTask = useCallback((taskId: string) => setSelectedTaskId(taskId), [])
  const clearSelection = useCallback(() => setSelectedTaskId(null), [])

  // ── Inline task creation (one creator visible at a time, anchored to a day) ──
  // New tasks from Demnächst are created as orphan tasks in the current
  // lifespace (projectId == null, lifeSpace == X). They used to be routed
  // through the quick-notes system project which has since been retired.
  const [creatingDateKey, setCreatingDateKey] = useState<string | null>(null)

  const handleStartCreate = useCallback((dateKeyStr: string) => {
    setCreatingDateKey(dateKeyStr)
    setEditingTaskId(null) // only one open editor/creator at a time
  }, [])

  const handleCancelCreate = useCallback(() => setCreatingDateKey(null), [])

  const handleSubmitCreate = useCallback(
    async (dateKeyStr: string, data: TaskCreationData) => {
      const [y, mo, d] = dateKeyStr.split('-').map(Number)
      if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return
      const startDate = new Date(y, mo - 1, d)
      await db.createTask({
        id: crypto.randomUUID(),
        title: data.title,
        description: data.description,
        status: 'todo',
        attention: data.attention ?? null,
        category: 'other',
        lifeSpace,
        projectId: null,
        sectionId: null,
        dueDate: data.dueDate ?? null,
        dueTime: data.dueTime ?? null,
        duePeriod: data.duePeriod ?? null,
        startDate,
        startTime: data.startTime ?? null,
        startPeriod: data.startPeriod ?? null,
        completedAt: null,
        estimatedDuration: data.estimatedDuration ?? null,
        actualDuration: null,
        durationMode: data.durationMode ?? null,
        tags: data.tags ?? [],
        parentTaskId: null,
        pausedAt: null,
      })
      scheduleSync()
      setCreatingDateKey(null)
    },
    [lifeSpace],
  )

  // Click outside any TaskEntry clears the selection (matches ProjectPage).
  // Skipping when a dropdown portal is open — those live outside our DOM
  // subtree and their pointerdowns shouldn't bubble into a deselect.
  const handlePagePointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('.task-entry')) return
    if (document.querySelector('[data-radix-popper-content-wrapper]')) return
    if (document.querySelector('[data-task-dropdown]')) return
    clearSelection()
  }, [clearSelection])

  // ── Keyboard navigation (Arrow Up/Down + Enter) ──
  // Flat list of task IDs in render order — the traversal set for arrow keys.
  const flatTaskIds = useMemo<string[]>(() => {
    const ids: string[] = []
    for (const m of months) {
      for (const d of m.days) {
        for (const task of d.tasks) ids.push(task.id)
      }
    }
    return ids
  }, [months])

  // Precomputed "which month owns which task" index. Used by the MonthBlock
  // render below so the O(days × tasks) `.some()` scan per month becomes an
  // O(1) `Set.has` lookup on `selectedTaskId` / `editingTaskId` — matters on
  // arrow-key navigation where selection flips every keystroke.
  const taskIdsByMonth = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const m of months) {
      const set = new Set<string>()
      for (const d of m.days) {
        for (const task of d.tasks) set.add(task.id)
      }
      map.set(m.monthKey, set)
    }
    return map
  }, [months])

  // Parallel index for the creator: which month owns which day-key.
  const dayKeysByMonth = useMemo(() => {
    const map = new Map<string, Set<string>>()
    for (const m of months) {
      const set = new Set<string>()
      for (const d of m.days) set.add(d.dateKey)
      map.set(m.monthKey, set)
    }
    return map
  }, [months])

  // Scroll just enough to bring `taskId` into the "safe" band — outside both
  // fade overlays plus a breathing margin. Using `scrollIntoView({ block:
  // 'nearest' })` would leave the row flush with the viewport edge, where
  // the fade gradient drowns it out.
  //
  // Smooth-scroll only when the delta is large enough that an instant jump
  // would feel jarring (~one row-height). For single-row arrow-key hops the
  // scroll is instant so rapid key presses don't stack a queue of ongoing
  // smooth-scrolls that visually trail behind the user's key input.
  const SMOOTH_SCROLL_THRESHOLD_PX = 60
  const scrollTaskIntoView = useCallback((taskId: string) => {
    const scrollEl = scrollRef.current
    if (!scrollEl) return
    const taskEl = scrollEl.querySelector<HTMLElement>(`[data-task-id="${taskId}"]`)
    if (!taskEl) return

    const rootRect = scrollEl.getBoundingClientRect()
    const taskRect = taskEl.getBoundingClientRect()
    const topSafe = rootRect.top + FADE_HEIGHT_PX + KEYBOARD_SCROLL_MARGIN_PX
    const bottomSafe = rootRect.bottom - FADE_HEIGHT_PX - KEYBOARD_SCROLL_MARGIN_PX

    let delta = 0
    if (taskRect.top < topSafe) {
      delta = taskRect.top - topSafe
    } else if (taskRect.bottom > bottomSafe) {
      delta = taskRect.bottom - bottomSafe
    }

    if (delta !== 0) {
      const behavior: ScrollBehavior =
        Math.abs(delta) > SMOOTH_SCROLL_THRESHOLD_PX ? 'smooth' : 'auto'
      scrollEl.scrollTo({ top: scrollEl.scrollTop + delta, behavior })
    }
  }, [])

  // Tab / Shift+Tab in expanded mode: save current edit (TaskEntry does that
  // itself before calling onNavigateNext/Prev) and open the adjacent task.
  // Boundaries close the editor instead of wrapping.
  //
  // We read `flatTaskIds` through a ref instead of as a useCallback dep, so
  // this callback keeps a stable identity across renders. Otherwise it would
  // change on every months/flatTaskIds rebuild and propagate through
  // MonthBlock → DayGroup as a "prop changed" re-render across the whole
  // page (60+ DayGroups), which we measured at ~960 wasted DayGroup renders
  // per single mutation.
  const flatTaskIdsRef = useRef<string[]>(flatTaskIds)
  useEffect(() => { flatTaskIdsRef.current = flatTaskIds }, [flatTaskIds])
  const handleNavigateEdit = useCallback(
    (taskId: string, direction: 'next' | 'prev') => {
      const ids = flatTaskIdsRef.current
      const idx = ids.indexOf(taskId)
      if (idx === -1) {
        setEditingTaskId(null)
        return
      }
      const nextIdx = direction === 'next' ? idx + 1 : idx - 1
      if (nextIdx < 0 || nextIdx >= ids.length) {
        setEditingTaskId(null)
        return
      }
      const nextId = ids[nextIdx]
      setEditingTaskId(nextId)
      setSelectedTaskId(nextId)
      scrollTaskIntoView(nextId)
    },
    [scrollTaskIntoView],
  )

  // First task whose row currently intersects the scroll viewport — the
  // starting point when arrow keys are pressed without an existing selection.
  const findFirstVisibleTaskId = useCallback((): string | null => {
    const scrollEl = scrollRef.current
    if (!scrollEl || flatTaskIds.length === 0) return null
    const rootRect = scrollEl.getBoundingClientRect()
    for (const id of flatTaskIds) {
      const el = scrollEl.querySelector<HTMLElement>(`[data-task-id="${id}"]`)
      if (!el) continue
      const r = el.getBoundingClientRect()
      if (r.bottom > rootRect.top && r.top < rootRect.bottom) return id
    }
    return flatTaskIds[0] ?? null
  }, [flatTaskIds])

  useEffect(() => {
    // Inactive tabs stay mounted in TabStack — skip listener entirely so
    // arrow/enter keys don't fire on hidden pages.
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      // Defer entirely to TaskEntry while a task is being edited.
      if (editingTaskId) return
      // Ignore when the user is typing somewhere else (title input, etc.) or
      // a portal-based menu/dropdown is open.
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      if (document.querySelector('[role="menu"]')) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (flatTaskIds.length === 0) return
        e.preventDefault()
        if (selectedTaskId === null) {
          const first = findFirstVisibleTaskId()
          if (first) {
            setSelectedTaskId(first)
            scrollTaskIntoView(first)
          }
          return
        }
        const idx = flatTaskIds.indexOf(selectedTaskId)
        if (idx === -1) {
          // Selection pointed at a task that is no longer in the list (e.g.
          // completed + dropped out of grace). Start over with the visible one.
          const first = findFirstVisibleTaskId()
          if (first) {
            setSelectedTaskId(first)
            scrollTaskIntoView(first)
          }
          return
        }
        const delta = e.key === 'ArrowDown' ? 1 : -1
        const nextIdx = Math.max(0, Math.min(flatTaskIds.length - 1, idx + delta))
        const nextId = flatTaskIds[nextIdx]
        setSelectedTaskId(nextId)
        scrollTaskIntoView(nextId)
        return
      }

      if (e.key === 'Enter' && selectedTaskId) {
        e.preventDefault()
        startEdit(selectedTaskId)
        return
      }

      // Space → toggle complete on the selected task.
      // Matches useTaskListInteraction / useProjectNavigation semantics.
      if (e.code === 'Space' && selectedTaskId) {
        e.preventDefault()
        wrappedToggleComplete(selectedTaskId)
        return
      }

      if (e.key === 'Escape' && selectedTaskId) {
        e.preventDefault()
        clearSelection()
        return
      }

      // Backspace / Delete → delete selected task and move focus to the
      // next (or previous if it was last) task. Matches TaskList's pattern
      // in useTaskListInteraction. `wrappedDelete` shows the undo toast.
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedTaskId) {
        e.preventDefault()
        const idx = flatTaskIds.indexOf(selectedTaskId)
        let nextId: string | null = null
        if (idx !== -1) {
          if (idx < flatTaskIds.length - 1) nextId = flatTaskIds[idx + 1]
          else if (idx > 0) nextId = flatTaskIds[idx - 1]
        }
        wrappedDelete(selectedTaskId)
        setSelectedTaskId(nextId)
        if (nextId) scrollTaskIntoView(nextId)
        return
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, editingTaskId, selectedTaskId, flatTaskIds, findFirstVisibleTaskId, scrollTaskIntoView, startEdit, clearSelection, wrappedDelete, wrappedToggleComplete])

  // ── Visible month via IntersectionObserver ──
  // Dedup by key — setSelectedMonth(new Date(...)) creates a fresh object
  // reference every call, and React uses Object.is, so without this guard
  // the MutationObserver + scroll listener would re-trigger setState every
  // frame even when the visible month hasn't changed, blowing the update-
  // depth limit.
  //
  // `programmaticTargetRef` is a navigation lock: chip-click and Cmd+Arrow
  // call `resetTo(month)` which mutates the lazy range. Mounting/unmounting
  // MonthBlocks shifts content above the user's scrollTop, so the IO
  // recompute that fires before our rAF scroll lands sees the wrong month
  // at the top and would otherwise reset `selectedMonth` back. We ignore
  // those bogus reports until the IO confirms the target month at least
  // once OR the safety timeout elapses (in case the target never becomes
  // visible — e.g. user scrolled away mid-navigation).
  const programmaticTargetRef = useRef<string | null>(null)
  const programmaticTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleVisibleChange = useCallback((key: string) => {
    const target = programmaticTargetRef.current
    if (target) {
      if (key === target) {
        // Confirmed — release the lock.
        programmaticTargetRef.current = null
        if (programmaticTimerRef.current) {
          clearTimeout(programmaticTimerRef.current)
          programmaticTimerRef.current = null
        }
      } else {
        // Suppress: the IO is reading a transient pre-scroll layout.
        return
      }
    }
    setVisibleMonthKey(prev => (prev === key ? prev : key))
    const [y, m] = key.split('-').map(Number)
    if (Number.isNaN(y) || Number.isNaN(m)) return
    setSelectedMonth(prev => {
      if (prev.getFullYear() === y && prev.getMonth() === m - 1) return prev
      return new Date(y, m - 1, 1)
    })
  }, [])

  /** Engage the navigation lock for a single programmatic month-jump.
   *  Suppresses the IO-driven `handleVisibleChange` and the lazy-extend-
   *  on-scroll listener while the page settles on the requested target. */
  const armProgrammaticNavigation = useCallback((targetKey: string) => {
    programmaticTargetRef.current = targetKey
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current)
    programmaticTimerRef.current = setTimeout(() => {
      programmaticTargetRef.current = null
      programmaticTimerRef.current = null
    }, 800)
    // Reset the auto-fill watermark so the new (potentially shorter)
    // range is allowed to extend down again until the viewport is full.
    // Otherwise the cumulative `prev > current` check from the previous
    // wider range would block extension forever — leaving a half-empty
    // page after navigating to a sparse month.
    prevAutoFillHeightRef.current = 0
  }, [])

  // Drop the timer on unmount.
  useEffect(() => () => {
    if (programmaticTimerRef.current) clearTimeout(programmaticTimerRef.current)
  }, [])

  useVisibleMonth({ scrollRef, anchorsRef, onChange: handleVisibleChange })

  // ── Lazy extension on scroll ──
  // Suppressed while a programmatic-navigation lock is engaged so the
  // scroll events fired by chip/keyboard navigation can't trigger
  // extendUp/extendDown — those would shift the DOM and yank the user's
  // viewport off the target month they just navigated to.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      if (programmaticTargetRef.current) return
      if (el.scrollTop < LAZY_LOAD_THRESHOLD_PX) extendUp()
      if (el.scrollTop + el.clientHeight > el.scrollHeight - LAZY_LOAD_THRESHOLD_PX) {
        extendDown()
      }
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [extendUp, extendDown])

  // ── Auto-fill: keep extending down until content fills the viewport ──
  // Without this, a tall window could show empty space below the last month
  // because onScroll never fires for content that's never been scrollable.
  // We gate the recursion on scrollHeight growth so a pathological month
  // producing zero pixels can't infinite-loop.
  //
  // Must be skipped when the tab is inactive inside TabStack — hidden slots
  // have `clientHeight === 0`, which makes `needsMore` permanently true and
  // sends extendDown into an infinite-setState loop (Maximum update depth).
  const prevAutoFillHeightRef = useRef(0)
  useLayoutEffect(() => {
    if (!isActive) return
    const el = scrollRef.current
    if (!el || months.length === 0) return
    if (el.clientHeight === 0) return // inactive/hidden slot, nothing to fill
    const needsMore = el.scrollHeight < el.clientHeight + LAZY_LOAD_THRESHOLD_PX
    const grew = el.scrollHeight > prevAutoFillHeightRef.current
    if (needsMore && grew) {
      prevAutoFillHeightRef.current = el.scrollHeight
      extendDown()
    }
  }, [isActive, months, extendDown])


  // ── Scroll anchor preservation on top-extend ──
  // When `range.start` moves earlier (new months prepended), the DOM grows
  // above the viewport. Without compensation, the user's viewport would jump.
  // We remember scroll height pre-layout and re-apply the delta after paint.
  const prevScrollHeightRef = useRef<number>(0)
  const prevRangeStartRef = useRef<number>(range.start.getTime())
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const startedEarlier = range.start.getTime() < prevRangeStartRef.current
    if (startedEarlier && prevScrollHeightRef.current > 0) {
      const delta = el.scrollHeight - prevScrollHeightRef.current
      if (delta > 0) el.scrollTop += delta
    }
    prevScrollHeightRef.current = el.scrollHeight
    prevRangeStartRef.current = range.start.getTime()
  }, [range.start, months])

  // ── Initial scroll position: today's day row ──
  // Runs synchronously with the commit that renders the first non-empty
  // month list. Mirrors `handleJumpToday`'s positioning: day row lands just
  // below the top fade overlay (instead of the month anchor), so the user
  // lands on "today" on mount — same behaviour as clicking the today chip.
  // MonthBlock registers its anchor via a ref callback (not useEffect), so
  // `anchorsRef` and the day `<div data-date="…">` elements are both already
  // in the DOM at this point — no rAF dance needed. Hard jump (no smooth
  // behaviour) on mount so the page appears already at the right position
  // instead of animating from the top.
  const didInitialScrollRef = useRef(false)
  useLayoutEffect(() => {
    if (didInitialScrollRef.current) return
    if (months.length === 0) return
    const el = scrollRef.current
    if (!el) return
    const todayMonthKey = monthKey(today)
    const todayKey = todayDateKey
    const dayEl = el.querySelector<HTMLElement>(`[data-date="${todayKey}"]`)
    if (dayEl) {
      const rootRect = el.getBoundingClientRect()
      const dayRect = dayEl.getBoundingClientRect()
      el.scrollTop += (dayRect.top - rootRect.top) - FADE_HEIGHT_PX - KEYBOARD_SCROLL_MARGIN_PX
    } else {
      // Fallback — today's row not yet mounted for some reason: align on
      // today's month anchor instead.
      const anchor = anchorsRef.current.get(todayMonthKey)
      if (!anchor) return
      scrollMonthIntoPosition(el, anchor)
    }
    // Pin title + chip highlight to today so the scroll-driven recompute
    // doesn't flash the previous month before settling.
    setVisibleMonthKey(todayMonthKey)
    didInitialScrollRef.current = true
  }, [months, today, todayDateKey])

  // ── Chip click: jump to the 1st of the chosen month in the day list ──
  const handleChipSelect = useCallback((month: Date) => {
    const targetKey = monthKey(month)
    armProgrammaticNavigation(targetKey)
    setSelectedMonth(month)
    setVisibleMonthKey(targetKey)
    // Padding=6 → 13-month window centred on the target. Empty months are
    // ~120px tall, so this guarantees the scroll container has enough
    // content to overflow even when the user lands deep in a task-free
    // region (otherwise only the 3 padded months render and the page
    // ends up un-scrollable until the user reaches a populated month).
    resetTo(month, 6)
    // Double rAF so the scroll lands AFTER the auto-fill useLayoutEffect
    // cascade has settled. resetTo can shrink the range to a sparse window
    // (e.g. far-back months with no tasks); auto-fill then extendDowns for
    // multiple commits until the viewport is full. A single rAF fires
    // mid-cascade and lands on a soon-to-be-stale layout; the second rAF
    // is queued after the cascade has stabilized.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = scrollRef.current
      const anchor = anchorsRef.current.get(targetKey)
      if (!el || !anchor) return
      scrollMonthIntoPosition(el, anchor)
    }))
  }, [resetTo, armProgrammaticNavigation])

  // ── Anchor registration ──
  const registerAnchor = useCallback((key: string, el: HTMLElement | null) => {
    if (el) anchorsRef.current.set(key, el)
    else anchorsRef.current.delete(key)
  }, [])

  // ── Derive visible month Date from key for MonthTitle ──
  const visibleMonth = useMemo(() => {
    const [y, m] = visibleMonthKey.split('-').map(Number)
    if (Number.isNaN(y) || Number.isNaN(m)) return today
    return new Date(y, m - 1, 1)
  }, [visibleMonthKey, today])

  // ── Task counts per month (for chip badges) ──
  // Derived only from months currently in the lazy-load window. Chips outside
  // that window show no count until the user scrolls them into view.
  const taskCountsByMonth = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = {}
    for (const m of months) {
      let total = 0
      for (const day of m.days) total += day.tasks.length
      counts[m.monthKey] = total
    }
    return counts
  }, [months])

  // ── Today memo: drives both the chip marker and Cmd+T ──
  const todayMonth = useMemo(() => startOfMonth(new Date()), [])

  // ── Jump to today: reset range, align anchor + chip selection, scroll the
  // day row into view below the top fade, and select the first task of the
  // day if there is one.
  const handleJumpToday = useCallback(() => {
    const now = new Date()
    const todayStartDate = startOfDay(now)
    const todayMonthDate = startOfMonth(now)
    const todayKeyStr = dateKey(todayStartDate)
    const todayMonthKeyStr = monthKey(todayMonthDate)
    armProgrammaticNavigation(todayMonthKeyStr)
    setSelectedMonth(todayMonthDate)
    setVisibleMonthKey(todayMonthKeyStr)
    // Padding=6 → guarantees a scrollable viewport even when surrounding
    // months are empty. See handleChipSelect comment for rationale.
    resetTo(todayMonthDate, 6)

    // Auto-select the first task of today (if any) so keyboard nav can pick
    // up right away. `months` here is the snapshot before the resetTo render
    // commits — that's fine, today's bucket is still in it because the lazy
    // range always includes today (initial center, extended outward only).
    const todayBucket = months.find(m => m.monthKey === monthKey(todayMonthDate))
    const todayDay = todayBucket?.days.find(d => d.dateKey === todayKeyStr)
    const firstTodayTaskId = todayDay?.tasks[0]?.id ?? null
    setSelectedTaskId(firstTodayTaskId)
    // Defer through two rAFs. `resetTo` may unmount/remount MonthBlocks for
    // the new range; the first rAF lets React commit that, the second lets
    // the MonthBlock ref callbacks register the fresh day rows in the DOM.
    // (Single rAF is sometimes too early and the querySelector misses.)
    const performScroll = () => {
      const scrollEl = scrollRef.current
      if (!scrollEl) return
      const todayKey = dateKey(todayStartDate)
      const dayEl = scrollEl.querySelector<HTMLElement>(`[data-date="${todayKey}"]`)
      if (dayEl) {
        const rootRect = scrollEl.getBoundingClientRect()
        const dayRect = dayEl.getBoundingClientRect()
        const targetTop =
          scrollEl.scrollTop + (dayRect.top - rootRect.top) - FADE_HEIGHT_PX - KEYBOARD_SCROLL_MARGIN_PX
        if (typeof scrollEl.scrollTo === 'function') {
          scrollEl.scrollTo({ top: targetTop, behavior: 'smooth' })
        } else {
          scrollEl.scrollTop = targetTop
        }
        return
      }
      // Fallback — day row not yet mounted: align on the month anchor instead.
      const anchor = anchorsRef.current.get(monthKey(todayMonthDate))
      if (anchor) scrollMonthIntoPosition(scrollEl, anchor)
    }
    requestAnimationFrame(() => requestAnimationFrame(performScroll))
  }, [resetTo, months, armProgrammaticNavigation])

  // ── Expand: reserve scroll-room + smooth-scroll into view ──
  // Mirrors TaskList's behaviour (src/renderer/features/taskManagement/
  // components/TaskList/TaskList.tsx). When a row near the bottom edge is
  // opened, its expanded card would clip under the fade overlay; we extend
  // the scrollable content's bottom margin by just enough so `scrollIntoView`
  // can bring the full card into view.
  const scrollInnerRef = useRef<HTMLDivElement>(null)
  const hadExpandMarginRef = useRef(false)

  useLayoutEffect(() => {
    if (!editingTaskId) return
    const scrollEl = scrollRef.current
    const innerEl = scrollInnerRef.current
    if (!scrollEl || !innerEl) return
    const wrapper = scrollEl.querySelector<HTMLElement>(`[data-task-id="${editingTaskId}"]`)
    if (!wrapper) return

    const EXPAND_DELTA = 150
    const wrapperRect = wrapper.getBoundingClientRect()
    const scrollRect = scrollEl.getBoundingClientRect()
    const overflow = (wrapperRect.bottom + EXPAND_DELTA) - scrollRect.bottom
    if (overflow <= 0) return

    const remainingScroll = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight
    if (remainingScroll >= overflow) return

    const marginNeeded = overflow - remainingScroll
    innerEl.style.transition = 'none'
    innerEl.style.marginBottom = `${marginNeeded}px`
    hadExpandMarginRef.current = true
  }, [editingTaskId])

  // Cleanup: once the task collapses, animate the reserved margin back to 0.
  useEffect(() => {
    if (editingTaskId || !hadExpandMarginRef.current) return
    hadExpandMarginRef.current = false
    const innerEl = scrollInnerRef.current
    if (!innerEl) return
    const currentMargin = parseFloat(innerEl.style.marginBottom) || 0
    if (currentMargin <= 0) {
      innerEl.style.marginBottom = ''
      innerEl.style.transition = ''
      return
    }
    innerEl.style.transition = 'margin-bottom 500ms cubic-bezier(0.65, 0, 0.35, 1)'
    const rafId = requestAnimationFrame(() => {
      innerEl.style.marginBottom = '0px'
    })
    const cleanupTimer = setTimeout(() => {
      innerEl.style.transition = ''
      innerEl.style.marginBottom = ''
    }, 520)
    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(cleanupTimer)
      innerEl.style.transition = ''
    }
  }, [editingTaskId])

  // Smooth-scroll the expanded task into view. Two passes:
  //  - 50ms: start scrolling while the expand animation is still running so
  //    the motion feels continuous with the card opening.
  //  - 300ms: final correction once the expanded card has reached its full
  //    height (framer-motion layout animation has settled).
  useEffect(() => {
    if (!editingTaskId) return
    const scrollEl = scrollRef.current
    if (!scrollEl) return
    const scrollTo = () => {
      const wrapper = scrollEl.querySelector<HTMLElement>(`[data-task-id="${editingTaskId}"]`)
      wrapper?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
    const t1 = setTimeout(scrollTo, 50)
    const t2 = setTimeout(scrollTo, 300)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [editingTaskId])

  // ── Year picker: jump to Jan of the chosen year ──
  const handleYearSelect = useCallback((year: number) => {
    const target = new Date(year, 0, 1)
    handleChipSelect(target)
  }, [handleChipSelect])

  // ── Drag-and-drop: drop a task on another day to re-schedule it ──
  // A 5px activation distance keeps click/double-click intact.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const [activeDragTaskId, setActiveDragTaskId] = useState<string | null>(null)
  const activeDragTask = useMemo<TaskEntryData | null>(() => {
    if (!activeDragTaskId) return null
    for (const m of months) {
      for (const day of m.days) {
        const t = day.tasks.find(t => t.id === activeDragTaskId)
        if (t) {
          return {
            id: t.id,
            title: t.title,
            completed: t.status === 'completed',
            attention: t.attention,
            description: t.description,
            estimatedDuration: t.estimatedDuration,
            durationMode: t.durationMode,
            startDate: t.startDate,
            startTime: t.startTime,
            startPeriod: t.startPeriod,
            dueDate: t.dueDate,
            dueTime: t.dueTime,
            duePeriod: t.duePeriod,
            tags: t.tags,
          }
        }
      }
    }
    return null
  }, [activeDragTaskId, months])

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveDragTaskId(e.active.id as string)
  }, [])

  // Index of currently-rendered tasks by id — lets handleDragEnd skip the
  // extra `db.getTask` IDB roundtrip on every drop. The upstream LiveQuery
  // already keeps this in sync; no risk of stale reads during a drag.
  const tasksById = useMemo(() => {
    const map = new Map<string, Task>()
    for (const m of months) for (const d of m.days) for (const t of d.tasks) map.set(t.id, t)
    return map
  }, [months])

  const handleDragEnd = useCallback(async (e: DragEndEvent) => {
    setActiveDragTaskId(null)
    const taskId = e.active.id as string
    const targetDateKey = e.over?.id as string | undefined
    if (!targetDateKey) return

    const original = tasksById.get(taskId)
    if (!original) return

    // Skip no-op drops (task already on this day).
    const currentEffective = original.startDate ?? original.dueDate
    if (currentEffective) {
      const currentKey = dateKey(startOfDay(currentEffective))
      if (currentKey === targetDateKey) return
    }

    const [y, mo, d] = targetDateKey.split('-').map(Number)
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return
    const newDate = new Date(y, mo - 1, d)

    // Update whichever date field the task already uses. `startDate` wins
    // (both are present → prefer startDate, matching effectiveDate()).
    const updates = original.startDate
      ? { startDate: newDate }
      : { dueDate: newDate }
    // Optimistic move FIRST so the task visibly jumps to the target day
    // in the next paint. The hook auto-clears the override once Dexie
    // catches up.
    setOptimisticTaskOverride(taskId, updates)
    await db.updateTask(taskId, updates)
    scheduleSync()
  }, [tasksById, setOptimisticTaskOverride])

  // ── Duplicate task (Cmd/Ctrl+D). Clone all fields from the original,
  // but reset running-state (status/completed/actualDuration) and drop
  // any paused state so the duplicate shows up in Upcoming next to its
  // source — not in Ruhend.
  const handleDuplicateTask = useCallback(async (taskId: string) => {
    const original = await db.getTask(taskId)
    if (!original) return
    const duplicate = await db.createTask(cloneTaskFields(original, {
      status: 'todo',
      completedAt: null,
      actualDuration: null,
      pausedAt: null,
    }))
    scheduleSync()
    setSelectedTaskId(duplicate.id)
  }, [])

  // ── Global keyboard shortcuts (Cmd/Ctrl+T, Cmd/Ctrl+Arrow) ──
  useEffect(() => {
    if (!isActive) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (editingTaskId) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      if (document.querySelector('[role="menu"]')) return
      const modifier = e.metaKey || e.ctrlKey
      if (!modifier) return

      if (e.key === 't' || e.key === 'T') {
        e.preventDefault()
        handleJumpToday()
        return
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        // Tab-switching uses Cmd+Ctrl+Arrow (and similar combos with
        // Shift/Alt). Only act on a clean Cmd-or-Ctrl + Arrow without
        // any extra modifier so we don't steal those shortcuts.
        if (e.shiftKey || e.altKey || (e.metaKey && e.ctrlKey)) return
        e.preventDefault()
        const delta = e.key === 'ArrowLeft' ? -1 : 1
        handleChipSelect(addMonths(selectedMonth, delta))
        return
      }
      // Cmd/Ctrl+D → duplicate selected task (matches ProjectPage shortcut).
      if ((e.key === 'd' || e.key === 'D') && selectedTaskId) {
        e.preventDefault()
        handleDuplicateTask(selectedTaskId)
        return
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isActive, editingTaskId, handleJumpToday, handleChipSelect, selectedMonth, selectedTaskId, handleDuplicateTask])

  return (
    <div className="task-view upcoming-page">
      <div className="upcoming-page__header">
        <h1 className="upcoming-page__title">
          <UpcomingOpenIcon size={18} className="upcoming-page__title-icon" />
          {t('upcoming.title')}
        </h1>
        <p className="upcoming-page__subtitle">{t('upcoming.subtitle')}</p>
      </div>

      <div className="upcoming-page__month-row">
        <MonthTitle month={visibleMonth} onYearSelect={handleYearSelect} />
        <MonthSelector
          selectedMonth={selectedMonth}
          todayMonth={todayMonth}
          taskCountsByMonth={taskCountsByMonth}
          onSelect={handleChipSelect}
          onJumpToday={handleJumpToday}
        />
      </div>

      <div className="upcoming-page__scroll-wrap" onPointerDown={handlePagePointerDown}>
        <DndContext
          sensors={dndSensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="upcoming-page__scroll" ref={scrollRef}>
            <div className="upcoming-page__scroll-inner" ref={scrollInnerRef}>
              {months.map(bucket => {
                // Narrow edit/select state per month so months that don't
                // contain the target task hit MonthBlock's React.memo. Without
                // this, every `editingTaskId` flip re-renders all 7+ months
                // of lazy-loaded content (visibly stutters on arrow-key
                // navigation). O(1) via the precomputed index maps.
                const monthTasks = taskIdsByMonth.get(bucket.monthKey)
                const monthDays = dayKeysByMonth.get(bucket.monthKey)
                const monthHasEditing = editingTaskId !== null && monthTasks?.has(editingTaskId) === true
                const monthHasSelected = selectedTaskId !== null && monthTasks?.has(selectedTaskId) === true
                const monthHasCreating = creatingDateKey !== null && monthDays?.has(creatingDateKey) === true
                return (
                  <MonthBlock
                    key={bucket.monthKey}
                    bucket={bucket}
                    registerAnchor={registerAnchor}
                    todayDateKey={todayDateKey}
                    editingTaskId={monthHasEditing ? editingTaskId : null}
                    selectedTaskId={monthHasSelected ? selectedTaskId : null}
                    creatingDateKey={monthHasCreating ? creatingDateKey : null}
                    onStartEdit={startEdit}
                    onCancelEdit={cancelEdit}
                    onNavigateEdit={handleNavigateEdit}
                    onSelectTask={selectTask}
                    onStartCreate={handleStartCreate}
                    onCancelCreate={handleCancelCreate}
                    onSubmitCreate={handleSubmitCreate}
                    onToggleComplete={wrappedToggleComplete}
                    onDelete={wrappedDelete}
                    onUpdateTask={handleUpdateTask}
                    onContextMenuBadgeUpdate={handleBadgeUpdate}
                  />
                )
              })}
            </div>
          </div>
          <DragOverlay dropAnimation={null}>
            {activeDragTask ? (
              <TaskEntry mode="collapsed" task={activeDragTask} startDateLabelMode="relative" />
            ) : null}
          </DragOverlay>
        </DndContext>
        <div className="upcoming-page__fade-top" aria-hidden="true" />
        <div className="upcoming-page__fade-bottom" aria-hidden="true" />
      </div>
    </div>
  )
}
